import React from 'react';
import { Link } from 'react-router-dom';
import { SEOHead } from '../components/SEOHead';
import { PageHero } from '../components/PageHero';
import { CheckCircle, ArrowRight, Zap, TrendingUp, Target, Users } from 'lucide-react';

const LEAD_PAKKETTEN = [
  { name: 'Starter Leads', leads: '10 leads', price: '€295', per: 'eenmalig', desc: 'Eerste stap naar een voorspelbare leadstroom.', features: ['10 gekwalificeerde leads','Branche-specifiek','Regio naar keuze','Binnen 14 werkdagen geleverd'] },
  { name: 'Business Leads', leads: '25 leads', price: '€595', per: 'eenmalig', desc: 'De meest gekozen optie voor MKB.', features: ['25 gekwalificeerde leads','Branche & regio filter','Contactgegevens compleet','Binnen 10 werkdagen','Kwaliteitsgarantie'], popular: true },
  { name: 'Growth Leads', leads: '50 leads', price: '€995', per: 'eenmalig', desc: 'Voor bedrijven die structureel willen groeien.', features: ['50 gekwalificeerde leads','Uitgebreide filtering','Prioriteit levering','Accountmanager begeleiding','Binnen 7 werkdagen'] },
  { name: 'Enterprise', leads: 'Op maat', price: 'Offerte', per: '', desc: 'Grootschalige leadgeneratie op abonnementsbasis.', features: ['Onbeperkte leads','Dedicated specialist','CRM-integratie','Maandelijkse levering','Full-funnel begeleiding'] },
];

const LeadsVisual = () => (
  <div className="space-y-3 w-full max-w-xs">
    {[['25 leads', 'Business pakket', '€595'], ['50 leads', 'Growth pakket', '€995'], ['10 leads', 'Starter', '€295']].map(([l, n, p]) => (
      <div key={n} className="bg-white/10 backdrop-blur rounded-xl p-4 flex items-center justify-between">
        <div>
          <p className="text-white font-bold text-sm">{l}</p>
          <p className="text-[#94a3b8] text-xs">{n}</p>
        </div>
        <span className="text-[#D4A017] font-bold text-sm">{p}</span>
      </div>
    ))}
  </div>
);

export default function LeadsPage() {
  return (
    <>
      <SEOHead
        title="Leads Kopen — Gekwalificeerde Zakelijke Leads | IMPERIA AI"
        description="Koop premium zakelijke leads voor uw branche en regio. Geen bulk, maar gekwalificeerde contacten klaar voor uw salesteam. Starter €295 — Business €595."
        keywords="leads kopen, zakelijke leads, leadgeneratie, b2b leads nederland, leads branche"
        canonical="/leads"
      />

      <PageHero
        badge="Leads kopen"
        title={<>Gekwalificeerde zakelijke leads — direct leverbaar</>}
        subtitle="Stop met wachten op klanten. Onze leadpakketten leveren u premium zakelijke leads voor uw branche en regio. Geen bulk, geen verspilling."
        ctaPrimary={{ label: 'Offerte aanvragen', to: '/offerte' }}
        ctaSecondary={{ label: 'Bekijk pakketten', to: '#pakketten' }}
        visual={<LeadsVisual />}
      />

      {/* Waarom premium leads */}
      <section className="bg-[#f8fafc] py-16">
        <div className="container-custom max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-serif text-3xl font-bold text-[#0f172a] mb-4">Premium leads, niet goedkope bulk</h2>
            <p className="text-[#64748b] max-w-xl mx-auto">Wij leveren uitsluitend gekwalificeerde zakelijke leads die passen bij uw branche, regio en doelgroep.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { icon: Target, title: 'Branche-specifiek', desc: 'Leads die passen bij uw specifieke sector en doelgroep.' },
              { icon: Users, title: 'Gekwalificeerd', desc: 'Alleen leads met echte interesse en koopintentie.' },
              { icon: Zap, title: 'Snel geleverd', desc: 'Binnen 7-14 werkdagen, afhankelijk van het pakket.' },
              { icon: TrendingUp, title: 'Regio op maat', desc: 'Uw stad, provincie of landelijk — u kiest het bereik.' },
            ].map(({ icon: Icon, title, desc }) => (
              <div key={title} className="bg-white rounded-2xl p-6 shadow-sm">
                <div className="w-10 h-10 bg-[#D4A017]/15 rounded-xl flex items-center justify-center mb-4">
                  <Icon className="w-5 h-5 text-[#D4A017]" />
                </div>
                <h3 className="font-bold text-[#0f172a] mb-2">{title}</h3>
                <p className="text-[#64748b] text-sm">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pakketten */}
      <section id="pakketten" className="bg-white py-16">
        <div className="container-custom max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-serif text-3xl font-bold text-[#0f172a] mb-4">Kies uw leadpakket</h2>
            <p className="text-[#64748b]">Alle pakketten inclusief contactgegevens, branche-filter en kwaliteitsgarantie.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {LEAD_PAKKETTEN.map(p => (
              <div key={p.name} className={`rounded-2xl p-6 ${p.popular ? 'bg-[#0f172a] border-2 border-[#D4A017] relative' : 'bg-[#f8fafc] border border-gray-200'}`}>
                {p.popular && <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#D4A017] text-[#0f172a] text-xs font-bold px-3 py-1 rounded-full">POPULAIR</div>}
                <p className={`text-xs font-bold uppercase tracking-wider mb-2 ${p.popular ? 'text-[#D4A017]' : 'text-[#64748b]'}`}>{p.name}</p>
                <p className={`text-2xl font-bold ${p.popular ? 'text-white' : 'text-[#0f172a]'}`}>{p.leads}</p>
                <p className={`text-xl font-bold ${p.popular ? 'text-[#D4A017]' : 'text-[#D4A017]'}`}>{p.price}</p>
                {p.per && <p className={`text-xs mb-4 ${p.popular ? 'text-[#94a3b8]' : 'text-[#64748b]'}`}>{p.per}</p>}
                <ul className="space-y-1.5 mb-5">
                  {p.features.map(f => (
                    <li key={f} className={`text-xs flex items-start gap-2 ${p.popular ? 'text-[#94a3b8]' : 'text-[#64748b]'}`}>
                      <CheckCircle className="w-3.5 h-3.5 text-[#D4A017] mt-0.5 flex-shrink-0" /> {f}
                    </li>
                  ))}
                </ul>
                <Link to="/offerte?dienst=Leads kopen&type=eenmalig" className={`block text-center font-bold py-2.5 rounded-xl text-sm transition-all ${p.popular ? 'bg-[#D4A017] text-[#0f172a] hover:bg-[#B8860B]' : 'bg-[#0f172a] text-white hover:bg-[#1e293b]'}`}>
                  {p.price === 'Offerte' ? 'Offerte aanvragen' : 'Bestel nu'}
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Leadmachine */}
      <section className="bg-[#D4A017] py-14">
        <div className="container-custom max-w-3xl mx-auto text-center">
          <h2 className="font-serif text-3xl font-bold text-[#0f172a] mb-4">Liever een complete leadmachine?</h2>
          <p className="text-[#1e293b] mb-8">Wij bouwen ook complete leadgeneratie-systemen — van advertenties tot CRM-koppeling. Automatisch, schaalbaar en meetbaar.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/lead-generatie" className="bg-[#0f172a] hover:bg-[#1e293b] text-white font-bold px-8 py-4 rounded-xl transition-all">
              Bekijk Leadmachine Setup <ArrowRight className="inline ml-2" size={16} />
            </Link>
            <Link to="/offerte" className="bg-[#0f172a]/20 hover:bg-[#0f172a]/30 text-[#0f172a] font-bold px-8 py-4 rounded-xl transition-all">
              Offerte aanvragen
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
